/**
 * /settings/security — TOTP enroll + disable. Cycle 17.
 *
 * Three states:
 *   - loading: still fetching status
 *   - enrolled: show "Disable" form (password gate)
 *   - not enrolled: show "Enable" flow (start → scan QR → enter code → save backup codes)
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

export const Route = createFileRoute("/_app/settings/security")({
  component: SecurityPage,
});

interface EnrollStartResponse {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

function SecurityPage() {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [enrollData, setEnrollData] = useState<EnrollStartResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setError(null);
    try {
      const r = await api.get<{ enrolled: boolean }>("/auth/totp/status");
      setEnrolled(r.data.enrolled);
    } catch {
      setError("Failed to load 2FA status");
      setEnrolled(false);
    }
  }

  async function startEnroll() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<EnrollStartResponse>(
        "/auth/totp/enroll-start",
        {},
      );
      setEnrollData(r.data);
      setEnrollCode("");
    } catch (err) {
      setError(extractError(err) ?? "Failed to start enrollment");
    } finally {
      setBusy(false);
    }
  }

  async function verifyEnroll(e: FormEvent) {
    e.preventDefault();
    if (busy || !enrollCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ backupCodes: string[] }>(
        "/auth/totp/enroll-verify",
        { code: enrollCode.trim() },
      );
      setBackupCodes(r.data.backupCodes);
      setEnrollData(null);
      setEnrolled(true);
    } catch (err) {
      setError(extractError(err) ?? "Bad code — try again");
    } finally {
      setBusy(false);
    }
  }

  async function disable(e: FormEvent) {
    e.preventDefault();
    if (busy || !disablePassword) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/totp/disable", { password: disablePassword });
      setEnrolled(false);
      setDisablePassword("");
      setBackupCodes(null);
    } catch (err) {
      setError(extractError(err) ?? "Disable failed — wrong password?");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Security</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Two-factor authentication adds a 6-digit code from your phone
        to every login. Store the backup codes in a password manager
        — they are the only way back in if you lose your phone.
      </p>

      {error && (
        <div className="brut-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {backupCodes && (
        <div
          className="brut-card"
          style={{ marginBottom: 24, padding: 16, background: "#defbe5" }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18 }}>
            Save your backup codes
          </h2>
          <p style={{ marginTop: 0 }}>
            Each code works once. We can&rsquo;t show these again.
          </p>
          <pre
            style={{
              background: "var(--paper)",
              padding: 12,
              border: "var(--border)",
              fontSize: 14,
              userSelect: "all",
            }}
          >
            {backupCodes.join("\n")}
          </pre>
        </div>
      )}

      {enrolled === null ? (
        <p>Loading…</p>
      ) : enrolled ? (
        <form onSubmit={disable} className="brut-card" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>2FA is enabled</h2>
          <label className="brut-label" htmlFor="dpw">
            Confirm password to disable
          </label>
          <input
            id="dpw"
            type="password"
            className="brut-input"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoComplete="current-password"
          />
          <button
            type="submit"
            className="brut-button is-danger"
            disabled={!disablePassword || busy}
            style={{ marginTop: 12 }}
          >
            {busy ? "Disabling…" : "Disable 2FA"}
          </button>
        </form>
      ) : enrollData ? (
        <form onSubmit={verifyEnroll} className="brut-card" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>
            Step 2: scan the QR code
          </h2>
          <p>
            Open Google Authenticator, 1Password, Authy, or another
            TOTP app. Scan this QR code, then enter the 6-digit code
            it generates.
          </p>
          <div style={{ textAlign: "center", margin: "0 0 12px" }}>
            <img
              src={enrollData.qrDataUrl}
              alt="TOTP QR code"
              width={220}
              height={220}
              style={{ border: "var(--border)" }}
            />
          </div>
          <p style={{ fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
            Or paste this secret manually:{" "}
            <code style={{ userSelect: "all" }}>{enrollData.secret}</code>
          </p>
          <label className="brut-label" htmlFor="code">
            6-digit code
          </label>
          <input
            id="code"
            type="text"
            className="brut-input"
            value={enrollCode}
            onChange={(e) => setEnrollCode(e.target.value)}
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
          />
          <button
            type="submit"
            className="brut-button"
            disabled={!enrollCode.trim() || busy}
            style={{ marginTop: 12 }}
          >
            {busy ? "Verifying…" : "Enable 2FA"}
          </button>
        </form>
      ) : (
        <div className="brut-card" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>2FA is disabled</h2>
          <p>
            Add a second factor to your login. Recommended for accounts
            with billing or admin access.
          </p>
          <button
            type="button"
            className="brut-button"
            disabled={busy}
            onClick={() => void startEnroll()}
          >
            {busy ? "Loading…" : "Enable 2FA"}
          </button>
        </div>
      )}
    </section>
  );
}

function extractError(err: unknown): string | null {
  const data = (err as { response?: { data?: { message?: string } } }).response
    ?.data;
  return data?.message ?? null;
}
